/** Même abréviations que `KkpNewsletterSemaineUtils::monthAbbrev` côté API. */
const NEWSLETTER_MONTH_ABBREV = ['Jan', 'Fév', 'Mar', 'avr.', 'mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'oct.', 'nov.', 'déc.'];

function pad2(value) {
  return String(value).padStart(2, '0');
}

function formatYmd(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function formatDmy(date) {
  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}`;
}

function startOfDay(date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function parseSemaineToDate(semaine, annee = null) {
  const label = String(semaine ?? '').trim();
  if (!label) {
    return null;
  }

  const dmy = label.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) {
    return startOfDay(new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1])));
  }

  const newsletter = label.match(/^(mer|ven)\.?\s+(\d{1,2})\s+([A-Za-zÀ-ÿ.]+)\s+(\d{2,4})$/iu);
  if (newsletter) {
    const monthMap = {
      jan: 0, fev: 1, fév: 1, mar: 2, avr: 3, mai: 4, jun: 5, juin: 5,
      jul: 6, juil: 6, aou: 7, aoû: 7, sep: 8, sept: 8, oct: 9, nov: 10, dec: 11, déc: 11,
    };
    const monthToken = newsletter[3].toLowerCase().replace(/\./g, '').normalize('NFD').replace(/\p{Diacritic}/gu, '');
    const month = monthMap[monthToken];
    if (month == null) {
      return null;
    }
    let year = Number(newsletter[4]);
    if (year < 100) {
      year += year >= 70 ? 1900 : 2000;
    }
    if (annee && Math.abs(year - annee) > 1) {
      year = annee;
    }
    return startOfDay(new Date(year, month, Number(newsletter[2])));
  }

  return null;
}

function firstWebMonday(annee) {
  const date = startOfDay(new Date(annee, 0, 5));
  while (date.getDay() !== 1) {
    date.setDate(date.getDate() + 1);
  }
  return date;
}

export function generateWebYearWeekOptions(annee) {
  const options = [];
  // Jusqu'au lundi strictement avant firstWebMonday(N+1) pour couvrir
  // les semaines orphelines (ex. 04/01/2027 entre fin 2026 et démarrage 2027).
  const start = firstWebMonday(annee);
  const nextYearStart = firstWebMonday(annee + 1);
  const cursor = new Date(start);

  while (cursor < nextYearStart) {
    options.push({
      value: formatDmy(cursor),
      label: formatDmy(cursor),
      date: formatYmd(cursor),
    });
    cursor.setDate(cursor.getDate() + 7);
  }

  return options;
}

export function generateNewsletterYearWeekOptions(annee) {
  const options = [];
  const cursor = startOfDay(new Date(annee, 0, 1));
  const end = startOfDay(new Date(annee, 11, 31));

  while (cursor <= end) {
    const weekday = cursor.getDay() === 0 ? 7 : cursor.getDay();
    if (weekday === 3 || weekday === 5) {
      const dayLabel = weekday === 3 ? 'mer' : 'ven';
      const semaine = `${dayLabel} ${cursor.getDate()} ${NEWSLETTER_MONTH_ABBREV[cursor.getMonth()]} ${String(cursor.getFullYear()).slice(-2)}`;
      options.push({
        value: semaine,
        label: semaine,
        date: formatYmd(cursor),
      });
    }
    cursor.setDate(cursor.getDate() + 1);
  }

  return options;
}

function optionCalendarDate(option, annee) {
  if (option?.date) {
    return startOfDay(new Date(`${option.date}T00:00:00`));
  }
  return parseSemaineToDate(option?.value, annee);
}

function sameCalendarDay(left, right) {
  return left instanceof Date && right instanceof Date && left.getTime() === right.getTime();
}

function includeMatchesOption(option, include, includeDate, annee) {
  if (include == null) {
    return false;
  }
  if (String(option.value) === include) {
    return true;
  }
  if (!includeDate) {
    return false;
  }
  return sameCalendarDay(optionCalendarDate(option, annee), includeDate);
}

function sortSemaineOptions(options, annee) {
  return [...options].sort((a, b) => {
    const dateA = optionCalendarDate(a, annee);
    const dateB = optionCalendarDate(b, annee);
    return (dateA?.getTime() ?? 0) - (dateB?.getTime() ?? 0);
  });
}

/**
 * Semaines du mois/année choisis, hors dates déjà passées.
 * `includeValue` conserve la semaine courante (édition) même si passée,
 * y compris quand le libellé stocké diffère de celui de la liste (`oct.` / `Oct`).
 */
export function filterSemaineOptionsForPeriod(options, {
  annee = null,
  mois = null,
  includeValue = null,
  fromDate = null,
} = {}) {
  const today = startOfDay(fromDate ?? new Date());
  const include = includeValue != null && String(includeValue).trim() !== ''
    ? String(includeValue)
    : null;
  const includeDate = include ? parseSemaineToDate(include, annee) : null;

  const filtered = (options ?? [])
    .filter((option) => {
      const date = optionCalendarDate(option, annee);

      if (!date) {
        return includeMatchesOption(option, include, includeDate, annee);
      }

      if (annee != null && Number(annee) > 0 && date.getFullYear() !== Number(annee)) {
        return false;
      }
      if (mois != null && Number(mois) > 0 && (date.getMonth() + 1) !== Number(mois)) {
        return false;
      }

      if (includeMatchesOption(option, include, includeDate, annee)) {
        return true;
      }

      return date.getTime() >= today.getTime();
    });

  if (include && !filtered.some((option) => includeMatchesOption(option, include, includeDate, annee))) {
    filtered.push({
      value: include,
      label: include,
      date: includeDate ? formatYmd(includeDate) : null,
    });
  }

  return sortSemaineOptions(filtered, annee);
}

/**
 * Ne vide jamais une semaine déjà saisie.
 * Si un autre libellé désigne le même jour, on retient celui de la liste.
 */
export function resolveKeptSemaine(current, options, annee = null) {
  const value = String(current ?? '').trim();
  if (!value) {
    return '';
  }
  if ((options ?? []).some((option) => String(option.value) === value)) {
    return value;
  }

  const currentDate = parseSemaineToDate(value, annee);
  if (!currentDate) {
    return value;
  }

  const sameDay = (options ?? []).find((option) => (
    sameCalendarDay(optionCalendarDate(option, annee), currentDate)
  ));
  return sameDay ? String(sameDay.value) : value;
}

export function rotateSemaineOptionsFromToday(options) {
  if (!options?.length) {
    return [];
  }

  const today = startOfDay(new Date());
  const sorted = [...options].sort((a, b) => {
    const dateA = a.date ? new Date(`${a.date}T00:00:00`) : parseSemaineToDate(a.value);
    const dateB = b.date ? new Date(`${b.date}T00:00:00`) : parseSemaineToDate(b.value);
    return (dateA?.getTime() ?? 0) - (dateB?.getTime() ?? 0);
  });

  let startIndex = 0;
  let nearestFutureDiff = Infinity;
  let nearestPastDiff = Infinity;
  let nearestPastIndex = 0;

  sorted.forEach((option, index) => {
    const date = option.date
      ? startOfDay(new Date(`${option.date}T00:00:00`))
      : parseSemaineToDate(option.value);
    if (!date) {
      return;
    }

    const diff = date.getTime() - today.getTime();
    if (diff >= 0 && diff < nearestFutureDiff) {
      nearestFutureDiff = diff;
      startIndex = index;
    }
    if (diff < 0 && Math.abs(diff) < nearestPastDiff) {
      nearestPastDiff = Math.abs(diff);
      nearestPastIndex = index;
    }
  });

  if (nearestFutureDiff === Infinity) {
    startIndex = nearestPastIndex;
  }

  return [...sorted.slice(startIndex), ...sorted.slice(0, startIndex)];
}

/**
 * @param {Array} ventes
 * @param {{ annee?: number|null, canal?: 'web'|'newsletter' }} [options]
 */
export function buildSemaineOptionsFromVentes(ventes, options = {}) {
  const { annee = null, annees = null, canal = 'web' } = options;
  const byKey = new Map();
  const yearList = Array.isArray(annees) && annees.length > 0
    ? annees
    : (annee ? [annee] : []);

  for (const vente of ventes ?? []) {
    if (!vente?.semaine) {
      continue;
    }

    const parsed = parseSemaineToDate(vente.semaine, vente.annee ?? annee);
    const dateKey = vente.semaine_date || (parsed ? formatYmd(parsed) : vente.semaine);
    if (!byKey.has(dateKey)) {
      byKey.set(dateKey, {
        value: vente.semaine,
        label: vente.semaine,
        date: vente.semaine_date || (parsed ? formatYmd(parsed) : null),
      });
    }
  }

  for (const year of yearList) {
    if (!year) {
      continue;
    }
    const generated = canal === 'newsletter'
      ? generateNewsletterYearWeekOptions(year)
      : generateWebYearWeekOptions(year);

    for (const option of generated) {
      const key = option.date || option.value;
      if (!byKey.has(key)) {
        byKey.set(key, option);
      }
    }
  }

  const sorted = Array.from(byKey.values()).sort((a, b) => {
    const dateA = a.date ? new Date(`${a.date}T00:00:00`) : parseSemaineToDate(a.value, annee);
    const dateB = b.date ? new Date(`${b.date}T00:00:00`) : parseSemaineToDate(b.value, annee);
    return (dateA?.getTime() ?? 0) - (dateB?.getTime() ?? 0);
  });

  return canal === 'newsletter' ? sorted : rotateSemaineOptionsFromToday(sorted);
}

export function getVenteWeekKey(vente) {
  return vente?.semaine_date || vente?.semaine || null;
}

/**
 * Clé de semaine la plus proche d'aujourd'hui (future prioritaire, sinon passée la plus récente).
 *
 * @param {Array} ventes
 * @param {number|null} [annee]
 */
export function findNearestWeekKey(ventes, annee = null) {
  const today = startOfDay(new Date());
  let nearestFutureKey = null;
  let nearestFutureDiff = Infinity;
  let nearestPastKey = null;
  let nearestPastDiff = Infinity;
  const seen = new Set();

  for (const vente of ventes ?? []) {
    const key = getVenteWeekKey(vente);
    if (!key || seen.has(key)) {
      continue;
    }
    seen.add(key);

    const date = vente.semaine_date
      ? startOfDay(new Date(`${vente.semaine_date}T00:00:00`))
      : parseSemaineToDate(vente.semaine, vente.annee ?? annee);
    if (!date) {
      continue;
    }

    const diff = date.getTime() - today.getTime();
    if (diff >= 0 && diff < nearestFutureDiff) {
      nearestFutureDiff = diff;
      nearestFutureKey = key;
    } else if (diff < 0 && Math.abs(diff) < nearestPastDiff) {
      nearestPastDiff = Math.abs(diff);
      nearestPastKey = key;
    }
  }

  return nearestFutureKey ?? nearestPastKey;
}
